import { AddIcon, CloseIcon, DragHandleIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Select,
  Text,
} from '@chakra-ui/react';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Select as ChakraSelect,
  chakraComponents,
  type GroupBase,
  type PlaceholderProps,
  type SingleValue,
} from 'chakra-react-select';

import type { SecondaryDimensionValues } from '@revelio/core';
import { FormatType } from '@revelio/replots';
import { IconButton } from '@revelio/ui';

import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { secondaryDimensionOptions } from '../PageConfigProvider/types/dimension';
import type {
  ColumnAlignment,
  PositionedItem,
  TableColumnConfig,
} from '../PageConfigProvider/types/item';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import { BaseSelectStyles, menuPortalStyles } from './BaseSelectStyles';
import labelStyles from './section-label.module.css';
import styles from './TableColumnsEditor.module.css';
import { getAvailableMetrics } from './utils/metric-mapping';
import {
  getColumnDimensionLabel,
  getPrimaryDimension,
} from './utils/table-columns';

// const AGGREGATION_OPTIONS: { value: ColumnAggregation; label: string }[] = [
//   { value: 'sum', label: 'Sum' },
//   { value: 'avg', label: 'Average' },
//   { value: 'min', label: 'Min' },
//   { value: 'max', label: 'Max' },
//   { value: 'count', label: 'Count' },
// ];

/** Default format applied to variable columns when none is configured. */
export const DEFAULT_VARIABLE_FORMAT = FormatType.DECIMAL;

const FORMAT_OPTIONS: { value: FormatType; label: string }[] = [
  { value: FormatType.SI, label: 'Compact (1.2K)' },
  { value: FormatType.INTEGER, label: 'Integer (1,234)' },
  { value: FormatType.DECIMAL, label: 'Decimal (1,234.5)' },
  { value: FormatType.PERCENTAGE, label: 'Percent (12%)' },
  { value: FormatType.CURRENCY, label: 'Currency ($1.2K)' },
];

const ALIGNMENT_OPTIONS: { value: ColumnAlignment; label: string }[] = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const DIMENSION_GROUP_LABEL = 'Dimension';

type DimensionOption = {
  value: SecondaryDimensionValues;
  label: string;
  kind: 'dimension';
};

type MetricOption = {
  value: MetricValues;
  label: string;
  kind: 'variable';
};

type ColumnSelectOption = DimensionOption | MetricOption;

/** Styles the add-column select so its closed state mimics an outline button. */
const ADD_COLUMN_SELECT_STYLES: typeof BaseSelectStyles = {
  ...BaseSelectStyles,
  container: (provided) => ({
    ...provided,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 0,
    gap: '8px',
    boxSizing: 'border-box',
    width: '100%',
    height: '32px',
    background: '#FFFFFF',
    border: 'none',
    borderRadius: '4px',
  }),
  control: (provided) => ({
    ...provided,
    minH: '32px',
    h: '32px',
    bg: 'white',
    borderWidth: '1px',
    borderColor: 'gray.200',
    borderRadius: 'md',
    cursor: 'pointer',
    _hover: { bg: 'gray.50' },
  }),
  valueContainer: (provided) => ({
    ...provided,
    justifyContent: 'center',
  }),
  placeholder: (provided) => ({
    ...provided,
    position: 'static',
    transform: 'none',
    m: 0,
  }),
  dropdownIndicator: (provided) => ({ ...provided, display: 'none' }),
  indicatorSeparator: (provided) => ({ ...provided, display: 'none' }),
};

/** Renders the add-column placeholder as a centered "+ Add Column" label. */
const AddColumnPlaceholder = (
  props: PlaceholderProps<
    ColumnSelectOption,
    false,
    GroupBase<ColumnSelectOption>
  >
) => (
  <chakraComponents.Placeholder {...props}>
    <Flex align="center" justify="center" gap="0.375rem" fontWeight={600}>
      <AddIcon boxSize={2.5} />
      Add Column
    </Flex>
  </chakraComponents.Placeholder>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="xs" fontWeight={600} mb="0.25rem" mt="0.75rem">
    {children}
  </Text>
);

type SortableColumnItemProps = {
  column: TableColumnConfig;
  isDisabled: boolean;
  columnSelectGroups: GroupBase<ColumnSelectOption>[];
  selectedOption: (column: TableColumnConfig) => ColumnSelectOption | null;
  onSelect: (column: TableColumnConfig, option: ColumnSelectOption) => void;
  onUpdate: (column: TableColumnConfig) => void;
  onRemove: (columnId: string) => void;
  headerLabel: (column: TableColumnConfig) => string;
};

/** A column row in the config panel, draggable to reorder via its handle. */
const SortableColumnItem = ({
  column,
  isDisabled,
  columnSelectGroups,
  selectedOption,
  onSelect,
  onUpdate,
  onRemove,
  headerLabel,
}: SortableColumnItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <AccordionItem
      ref={setNodeRef}
      style={style}
      border="1px solid #E2E8F0"
      borderRadius="4px"
      mb="0.5rem"
      bg="white"
      boxShadow={isDragging ? 'md' : undefined}
      sx={{
        '& .chakra-collapse': {
          overflow: 'visible !important',
        },
      }}
    >
      <Flex align="center" pr="0.5rem">
        <IconButton
          aria-label="Drag to reorder column"
          icon={<DragHandleIcon boxSize={2.5} />}
          size="sm"
          variant="ghost"
          shape="rounded"
          className={styles.dragHandle}
          disabled={isDisabled}
          {...attributes}
          {...listeners}
        />
        <AccordionButton flex="1" py="0.5rem">
          <Box flex="1" textAlign="left" fontSize="sm" fontWeight={500}>
            {headerLabel(column)}
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <IconButton
          aria-label="Remove column"
          icon={<CloseIcon boxSize={2} />}
          size="sm"
          variant="ghost"
          shape="rounded"
          disabled={isDisabled}
          onClick={() => onRemove(column.id)}
        />
      </Flex>

      <AccordionPanel pb="0.75rem" pt={0} overflow="visible">
        <SectionLabel>Column</SectionLabel>
        <ChakraSelect<ColumnSelectOption, false, GroupBase<ColumnSelectOption>>
          chakraStyles={BaseSelectStyles}
          menuPortalTarget={document.body}
          styles={menuPortalStyles}
          options={columnSelectGroups}
          value={selectedOption(column)}
          isDisabled={isDisabled}
          isSearchable
          isClearable={false}
          onChange={(opt: SingleValue<ColumnSelectOption>) => {
            if (!opt) return;
            onSelect(column, opt);
          }}
        />

        {column.kind === 'variable' ? (
          <>
            <SectionLabel>Format</SectionLabel>
            <Select
              size="sm"
              value={column.format ?? DEFAULT_VARIABLE_FORMAT}
              isDisabled={isDisabled}
              onChange={(e) =>
                onUpdate({
                  ...column,
                  format: e.target.value as FormatType,
                })
              }
            >
              {FORMAT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </>
        ) : (
          <>
            <SectionLabel>Alignment</SectionLabel>
            <Select
              size="sm"
              value={column.alignment}
              isDisabled={isDisabled}
              onChange={(e) =>
                onUpdate({
                  ...column,
                  alignment: e.target.value as ColumnAlignment,
                })
              }
            >
              {ALIGNMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </>
        )}
      </AccordionPanel>
    </AccordionItem>
  );
};

export const TableColumnsEditor = ({
  item,
  isDisabled = false,
}: {
  item: PositionedItem;
  isDisabled?: boolean;
}) => {
  const { dispatch, primaryFilter } = useTabContentConfig();
  const primaryFilterId = primaryFilter?.id;
  const id = item.id;
  const columns = item.plot?.tableColumns ?? [];

  const metricGroups =
    getAvailableMetrics({ primaryFilterId, chartType: 'table' }) ?? [];
  const flatMetricOptions = metricGroups.flatMap((group) => group.options);
  const metricLabel = (metric: MetricValues): string =>
    flatMetricOptions.find((opt) => opt.value === metric)?.label ?? metric;

  // Surface the page's primary-filter dimension first; keep the rest in order.
  const primaryDimension = primaryFilterId
    ? getPrimaryDimension(primaryFilterId)
    : undefined;
  const sortedDimensionOptions = [...secondaryDimensionOptions].sort((a, b) => {
    if (a.value === primaryDimension) return -1;
    if (b.value === primaryDimension) return 1;
    return 0;
  });

  const dimensionGroup: GroupBase<DimensionOption> = {
    label: DIMENSION_GROUP_LABEL,
    options: sortedDimensionOptions.map(
      (opt): DimensionOption => ({
        value: opt.value,
        label: getColumnDimensionLabel(opt.value),
        kind: 'dimension',
      })
    ),
  };

  const metricGroupOptions: GroupBase<MetricOption>[] = metricGroups.map(
    (group) => ({
      label: group.label,
      options: group.options.map(
        (opt): MetricOption => ({
          value: opt.value,
          label: opt.label,
          kind: 'variable',
        })
      ),
    })
  );

  const columnSelectGroups = [dimensionGroup, ...metricGroupOptions];

  const addColumn = (option: ColumnSelectOption) => {
    const column: TableColumnConfig =
      option.kind === 'dimension'
        ? {
            id: crypto.randomUUID(),
            kind: 'dimension',
            dimension: option.value,
            alignment: 'left',
            label: getColumnDimensionLabel(option.value),
          }
        : {
            id: crypto.randomUUID(),
            kind: 'variable',
            metric: option.value,
            aggregation: 'sum',
            label: metricLabel(option.value),
          };
    dispatch({ type: 'ADD_TABLE_COLUMN', payload: { id, column } });
  };

  const updateColumn = (column: TableColumnConfig) =>
    dispatch({ type: 'UPDATE_TABLE_COLUMN', payload: { id, column } });

  const removeColumn = (columnId: string) =>
    dispatch({ type: 'REMOVE_TABLE_COLUMN', payload: { id, columnId } });

  const handleSelect = (
    column: TableColumnConfig,
    option: ColumnSelectOption
  ) => {
    if (option.kind === 'dimension') {
      updateColumn({
        ...(column.kind === 'dimension' ? column : {}),
        id: column.id,
        kind: option.kind,
        dimension: option.value,
        label: option.label,
      });
    } else {
      updateColumn({
        ...(column.kind === 'variable' ? column : {}),
        id: column.id,
        kind: option.kind,
        metric: option.value,
        label: option.label,
      });
    }
  };

  const selectedOption = (
    column: TableColumnConfig
  ): ColumnSelectOption | null => {
    const value =
      column.kind === 'dimension' ? column.dimension : column.metric;

    if (column.kind === 'dimension') {
      return dimensionGroup.options.find((opt) => opt.value === value) ?? null;
    }
    return (
      metricGroupOptions
        .flatMap((group) => group.options)
        .find((option) => option.value === value) ?? null
    );
  };

  const headerLabel = (column: TableColumnConfig): string => {
    if (column.label) return column.label;
    if (column.kind === 'dimension') {
      return getColumnDimensionLabel(column.dimension);
    }
    return metricLabel(column.metric);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = columns.map((c) => c.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const columnIds = arrayMove(ids, oldIndex, newIndex);
    dispatch({ type: 'REORDER_TABLE_COLUMNS', payload: { id, columnIds } });
  };

  return (
    <Box marginBottom="1.5rem">
      <Text
        className={labelStyles.sectionLabel}
        fontSize="sm"
        fontWeight={600}
        mb="0.5rem"
      >
        Columns
      </Text>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={columns.map((column) => column.id)}
          strategy={verticalListSortingStrategy}
        >
          <Accordion allowMultiple>
            {columns.map((column) => (
              <SortableColumnItem
                key={column.id}
                column={column}
                isDisabled={isDisabled}
                columnSelectGroups={columnSelectGroups}
                selectedOption={selectedOption}
                onSelect={handleSelect}
                onUpdate={updateColumn}
                onRemove={removeColumn}
                headerLabel={headerLabel}
              />
            ))}
          </Accordion>
        </SortableContext>
      </DndContext>

      <ChakraSelect<ColumnSelectOption, false, GroupBase<ColumnSelectOption>>
        chakraStyles={ADD_COLUMN_SELECT_STYLES}
        menuPortalTarget={document.body}
        styles={menuPortalStyles}
        components={{ Placeholder: AddColumnPlaceholder }}
        options={columnSelectGroups}
        value={null}
        placeholder="Add Column"
        isDisabled={isDisabled}
        isSearchable
        isClearable={false}
        controlShouldRenderValue={false}
        onChange={(opt: SingleValue<ColumnSelectOption>) => {
          if (!opt) return;
          addColumn(opt);
        }}
      />
    </Box>
  );
};
